import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toTitleCase } from '../utils/dateUtils';
import { getPatientDetailsPath } from '../utils/patientNavigation';

const parseDate = (value) => {
  if (!value) return 0;
  const [datePart, timePart = '00:00'] = value.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute).getTime();
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const getComparableValue = (patient, key) => {
  switch (key) {
    case 'name':
      return patient.name.toLowerCase();
    case 'phone':
      return patient.phone.replace(/\D/g, '');
    case 'lastVisit':
      if (patient.lastVisitTimestamp) return patient.lastVisitTimestamp;
      return parseDate(patient.lastVisit);
    case 'visitsResume':
      if (typeof patient.consultationsCount === 'number') return patient.consultationsCount;
      return parseFloat(patient.visitsResume.split('/')[0]) || 0;
    default:
      return patient[key];
  }
};

const AVATAR_PALETTES = [
  { bg: '#FBE0E5', fg: '#9F4A60' },
  { bg: '#EFE3F0', fg: '#7A4A86' },
  { bg: '#FBEEDF', fg: '#A66730' },
  { bg: '#E5DCEC', fg: '#634A82' },
  { bg: '#F4DBDE', fg: '#A8556A' },
  { bg: '#E6F1EB', fg: '#3D8068' },
  { bg: '#E8E3DA', fg: '#65564A' },
];

const getInitials = (name) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarTone = (name) => {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
};

const daysSince = (timestamp) => {
  if (!timestamp) return null;
  return Math.floor((Date.now() - timestamp) / 86400000);
};

const getStatusChip = (status) => {
  if (status === 'inactive') return { bg: '#F2F2F7', fg: '#8E8E93', label: 'Inativo' };
  return { bg: '#EFE3F0', fg: '#7A4A86', label: 'Ativo' };
};

const getDaysChip = (dias) => {
  if (dias >= 30) return { bg: '#FBE0E5', fg: '#9F4A60', label: `há ${dias} dias` };
  if (dias >= 14) return { bg: '#FBEEDF', fg: '#A66730', label: `há ${dias} dias` };
  return { bg: '#F2F2F7', fg: '#6E6E73', label: `há ${dias} dias` };
};

const SortHeader = ({ children, onClick }) => (
  <div
    onClick={onClick}
    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none' }}
  >
    {children}
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
      <path d="M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6" stroke="#A8A8AE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const btnAccent = {
  fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
  height: 40, padding: '0 16px', borderRadius: 10,
  border: '1px solid var(--button-color)', background: 'var(--button-color)', color: '#FFFFFF',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(var(--button-rgb),0.18), 0 6px 16px -8px rgba(var(--button-rgb),0.32)',
};
const btnFilter = {
  fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
  height: 40, padding: '0 14px', borderRadius: 10,
  border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(28,28,30,0.04)',
};
const btnRowAction = {
  fontFamily: 'inherit', fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em',
  padding: '6px 12px', borderRadius: 8,
  border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E',
  cursor: 'pointer', whiteSpace: 'nowrap',
};
const pageBtn = {
  fontFamily: 'inherit', fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em',
  padding: '5px 10px', borderRadius: 6,
  border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E',
  cursor: 'pointer',
};

export default function Pacientes() {
  const { patients, onUpdatePatient } = useOutletContext();
  const safePatients = Array.isArray(patients) ? patients : [];
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filtro, setFiltro] = useState('ativos');

  const handleFinalize = async (patient) => {
    if (!window.confirm(`Deseja finalizar o atendimento de ${patient.name}? O paciente ficará como inativo.`)) return;
    await onUpdatePatient({ ...patient, status: 'inactive' });
  };

  const handleReactivate = async (patient) => {
    await onUpdatePatient({ ...patient, status: 'active' });
  };

  const patientsWithConsultInfo = useMemo(() => {
    return safePatients.map((patient) => {
      const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
      let lastConsultationDate = null;
      let lastConsultationTime = '';

      consultations.forEach((consultation) => {
        if (!consultation?.date) return;
        const dateStr = String(consultation.date).split('T')[0];
        const [y, m, d] = dateStr.split('-').map(Number);
        if (!y || !m || !d) return;
        const current = new Date(y, m - 1, d);
        if (Number.isNaN(current.getTime())) return;
        if (!lastConsultationDate || current > lastConsultationDate) {
          lastConsultationDate = current;
          lastConsultationTime = consultation.time || '';
        }
      });

      const consultationsCount = consultations.length
        ? consultations.length
        : (() => {
            if (!patient.visitsResume) return 0;
            const parsed = parseFloat(String(patient.visitsResume).split('/')[0]);
            return Number.isNaN(parsed) ? 0 : parsed;
          })();

      const lastVisitTimestamp = lastConsultationDate
        ? lastConsultationDate.getTime()
        : parseDate(patient.lastVisit);

      const dateLabel = lastConsultationDate
        ? `${String(lastConsultationDate.getDate()).padStart(2, '0')}/${String(lastConsultationDate.getMonth() + 1).padStart(2, '0')}/${lastConsultationDate.getFullYear()}`
        : null;

      return {
        ...patient,
        consultationsCount,
        lastVisitDisplay: dateLabel
          ? lastConsultationTime ? `${dateLabel} ${lastConsultationTime}` : dateLabel
          : patient.lastVisit || '',
        lastVisitTimestamp,
        dias: daysSince(lastVisitTimestamp),
      };
    });
  }, [safePatients]);

  const filteredPatients = useMemo(() => {
    if (!query) return patientsWithConsultInfo;
    const lower = query.toLowerCase();
    return patientsWithConsultInfo.filter((patient) =>
      Object.values(patient).some((value) => String(value ?? '').toLowerCase().includes(lower))
    );
  }, [patientsWithConsultInfo, query]);

  const sortedPatients = useMemo(() => {
    const sorted = [...filteredPatients];
    sorted.sort((a, b) => {
      const aValue = getComparableValue(a, sortConfig.key);
      const bValue = getComparableValue(b, sortConfig.key);
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPatients, sortConfig]);

  const activePatients = useMemo(() => sortedPatients.filter((p) => p.status !== 'inactive'), [sortedPatients]);
  const inactivePatients = useMemo(() => sortedPatients.filter((p) => p.status === 'inactive'), [sortedPatients]);

  const visible = useMemo(() => {
    if (filtro === 'ativos') return activePatients;
    if (filtro === 'inativos') return inactivePatients;
    return sortedPatients;
  }, [filtro, activePatients, inactivePatients, sortedPatients]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { key, direction: 'asc' };
    });
  };

  const totalAtivos = activePatients.length;
  const totalInativos = inactivePatients.length;
  const total = totalAtivos + totalInativos;

  return (
    <div className="patients-card" style={{ paddingTop: 44, paddingBottom: 56, color: '#1C1C1E' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#FBE0E5', color: '#9F4A60',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
            Pacientes da clínica
          </div>
          <h1 style={{
            fontWeight: 700, fontSize: 38, lineHeight: 1.18,
            letterSpacing: '-0.035em', margin: '12px 0 0', color: '#1C1C1E',
          }}>
            Pacientes
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
            <span style={{ fontWeight: 600, color: '#1C1C1E' }}>{total} pacientes</span>
            {' · '}{totalAtivos} ativos
            {' · '}{totalInativos} {totalInativos === 1 ? 'inativo' : 'inativos'}
          </div>
        </div>
        <button style={btnAccent} onClick={() => navigate('/pacientes/novo')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
            <path d="M12 5v14M5 12h14" />
          </svg>
          Novo paciente
        </button>
      </div>

      {/* Search + filter buttons */}
      <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
        <div style={{
          flex: 1, height: 40, position: 'relative',
          background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 10,
          display: 'flex', alignItems: 'center', padding: '0 14px',
          boxShadow: '0 1px 2px rgba(28,28,30,0.04)',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou data…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1, border: 'none', outline: 'none',
              padding: '0 12px', fontSize: 13, color: '#1C1C1E',
              background: 'transparent', fontFamily: 'inherit', letterSpacing: '-0.005em',
            }}
          />
        </div>
        <button style={btnFilter}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
            <path d="M3 6h18M6 12h12M10 18h4" />
          </svg>
          Filtros
        </button>
        <button style={btnFilter}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          Exportar
        </button>
      </div>

      {/* Segmented control + count */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, padding: 3, background: '#F2F2F7', borderRadius: 10 }}>
          {[
            { id: 'ativos',   label: 'Ativos',   count: totalAtivos },
            { id: 'inativos', label: 'Inativos',  count: totalInativos },
            { id: 'todos',    label: 'Todos',     count: total },
          ].map((opt) => {
            const on = filtro === opt.id;
            return (
              <button key={opt.id} onClick={() => setFiltro(opt.id)} style={{
                fontFamily: 'inherit', border: 'none', cursor: 'pointer',
                padding: '6px 14px', borderRadius: 7,
                background: on ? '#FFFFFF' : 'transparent',
                color: on ? '#1C1C1E' : '#6E6E73',
                fontSize: 13, fontWeight: 600,
                boxShadow: on ? '0 1px 2px rgba(28,28,30,0.08)' : 'none',
                letterSpacing: '-0.005em',
                display: 'inline-flex', alignItems: 'center', gap: 7,
              }}>
                {opt.label}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: on ? '#9F4A60' : '#8E8E93',
                  background: on ? '#FBE0E5' : '#E5E5EA',
                  padding: '1px 7px', borderRadius: 999,
                  fontVariantNumeric: 'tabular-nums',
                }}>{opt.count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 12, color: '#8E8E93', letterSpacing: '-0.005em' }}>
          {visible.length} {visible.length === 1 ? 'paciente' : 'pacientes'}
        </div>
      </div>

      {/* Table */}
      <div style={{
        marginTop: 20,
        background: '#FFFFFF',
        border: '1px solid #E5E5EA',
        borderRadius: 22,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)',
      }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2.2fr 1.2fr 1.3fr 1fr 160px',
          gap: 16,
          padding: '14px 22px',
          background: '#FAFAFB',
          borderBottom: '1px solid #E5E5EA',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          color: '#6E6E73', alignItems: 'center',
        }}>
          <SortHeader onClick={() => handleSort('name')}>Nome</SortHeader>
          <SortHeader onClick={() => handleSort('phone')}>Telefone</SortHeader>
          <SortHeader onClick={() => handleSort('lastVisit')}>Última consulta</SortHeader>
          <SortHeader onClick={() => handleSort('visitsResume')}>Consultas</SortHeader>
          <div style={{ textAlign: 'right' }}>Ações</div>
        </div>

        {visible.length === 0 && (
          <div style={{ padding: '32px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>
            Nenhum paciente encontrado.
          </div>
        )}

        {visible.map((patient, i) => {
          const isInativo = patient.status === 'inactive';
          const av = avatarTone(patient.name);
          const stat = getStatusChip(patient.status);
          const days = (!isInativo && patient.dias != null) ? getDaysChip(patient.dias) : null;
          const displayName = toTitleCase(patient.name);

          return (
            <div key={patient._id || patient.id || i} style={{
              display: 'grid',
              gridTemplateColumns: '2.2fr 1.2fr 1.3fr 1fr 160px',
              gap: 16,
              padding: '16px 22px',
              borderTop: i === 0 ? 'none' : '1px solid #F2F2F7',
              alignItems: 'center',
              opacity: isInativo ? 0.85 : 1,
            }}>
              {/* Nome + avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 999, flexShrink: 0,
                  background: isInativo ? '#F2F2F7' : av.bg,
                  color: isInativo ? '#8E8E93' : av.fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em',
                }}>
                  {getInitials(patient.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    onClick={() => navigate(getPatientDetailsPath(patient?._id), { state: patient })}
                    style={{
                      fontSize: 14, fontWeight: 600, color: '#1C1C1E',
                      letterSpacing: '-0.008em', lineHeight: 1.25,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {displayName}
                  </div>
                  <div style={{
                    marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10.5, fontWeight: 600, color: stat.fg, background: stat.bg,
                    padding: '2px 7px', borderRadius: 999, letterSpacing: '-0.005em',
                  }}>
                    <span style={{ width: 4, height: 4, borderRadius: 999, background: stat.fg }} />
                    {stat.label}
                  </div>
                </div>
              </div>

              {/* Telefone */}
              <div style={{
                fontSize: 13.5, fontWeight: 500, color: '#1C1C1E',
                letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
              }}>
                {patient.phone || '—'}
              </div>

              {/* Última consulta */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <div style={{
                  fontSize: 13.5, fontWeight: 500,
                  color: patient.lastVisitDisplay ? '#1C1C1E' : '#C7C7CC',
                  letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums',
                }}>
                  {patient.lastVisitDisplay || '—'}
                </div>
                {days && (
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: days.fg, background: days.bg,
                    padding: '2px 8px', borderRadius: 999, width: 'fit-content', letterSpacing: '-0.005em',
                  }}>
                    {days.label}
                  </div>
                )}
              </div>

              {/* Consultas */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                {patient.consultationsCount > 0 ? (
                  <>
                    <span style={{
                      fontSize: 18, fontWeight: 700, color: '#1C1C1E',
                      letterSpacing: '-0.028em', fontVariantNumeric: 'tabular-nums',
                    }}>{patient.consultationsCount}</span>
                    <span style={{ fontSize: 12, color: '#8E8E93', letterSpacing: '-0.005em' }}>
                      {patient.consultationsCount === 1 ? 'consulta' : 'consultas'}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 16, color: '#C7C7CC', fontWeight: 500 }}>—</span>
                )}
              </div>

              {/* Ações */}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button
                  style={btnRowAction}
                  onClick={() => navigate(getPatientDetailsPath(patient?._id), { state: patient })}
                >
                  Ver
                </button>
                {isInativo ? (
                  <button
                    style={{ ...btnRowAction, color: '#3D8068', borderColor: 'rgba(61,128,104,0.25)' }}
                    onClick={() => handleReactivate(patient)}
                  >
                    Reativar
                  </button>
                ) : (
                  <button
                    style={{ ...btnRowAction, color: '#9F4A60', borderColor: 'rgba(159,74,96,0.25)' }}
                    onClick={() => handleFinalize(patient)}
                  >
                    Finalizar
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div style={{
          padding: '14px 22px',
          borderTop: '1px solid #F2F2F7',
          background: '#FAFAFB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 12, color: '#6E6E73', letterSpacing: '-0.005em',
        }}>
          <span>Mostrando {visible.length} de {visible.length} pacientes</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={pageBtn} disabled>← Anterior</button>
            <button style={{ ...pageBtn, background: '#1C1C1E', color: '#FFF', border: '1px solid #1C1C1E' }}>1</button>
            <button style={pageBtn}>Próxima →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
